import { useContext } from "react";
import { PrivilegeContext } from "../components/priviledge/PriviledgeProvider";
import { DropdownMenuItemsType, DropdownMenuItemType, FacilitatorAccessProfile, PermissionRequired, PermissionsRequired } from "../type/menu-type";
import { canAccessFacilitatorDashboard } from "./dashboardAccess";

// ----------------------------------------------------------
/**
 * EN: Check if Parent Menu has access
 * ES: Verifica si el menú padre tiene acceso
 * 
 * @param menu menu to ckech permissions
 * @returns boolean
 */
// ----------------------------------------------------------
export function CheckMenuItemAccess(
    permissionsRequired: PermissionsRequired | undefined,
    permissionsAnyOf?: PermissionsRequired | undefined,
    facilitatorAccess?: FacilitatorAccessProfile[] | undefined,
    dropdownItems?: DropdownMenuItemsType | undefined | null,
    ref?: string): boolean {
    const { userCan, isFacilitator, isFacilitatorManager } = useContext(PrivilegeContext);
    const checkAllPermissions = (permissions?: PermissionsRequired) => {
        if (!permissions || permissions.length === 0) {
            return true;
        }

        return permissions.every((permission: PermissionRequired) =>
            userCan(permission.action, permission.group, permission.isSuperAdmin || false)
        );
    };

    const checkAnyPermission = (permissions?: PermissionsRequired) => {
        if (!permissions || permissions.length === 0) {
            return true;
        }

        return permissions.some((permission: PermissionRequired) =>
            userCan(permission.action, permission.group, permission.isSuperAdmin || false)
        );
    };

    const checkFacilitatorAccess = (profiles?: FacilitatorAccessProfile[]) => {
        if (!profiles || profiles.length === 0) {
            return true;
        }

        return profiles.some((profile) => {
            if (profile === 'facilitator_manager') {
                return isFacilitatorManager;
            }

            if (profile === 'facilitator') {
                return canAccessFacilitatorDashboard(userCan, { isFacilitator, isFacilitatorManager });
            }

            return false;
        });
    };

    const checkVisibility = (
        requiredPermissions?: PermissionsRequired,
        anyOfPermissions?: PermissionsRequired,
        facilitatorProfiles?: FacilitatorAccessProfile[],
    ) => {
        const permissionAccess = checkAllPermissions(requiredPermissions)
            && checkAnyPermission(anyOfPermissions);

        if (!facilitatorProfiles || facilitatorProfiles.length === 0) {
            return permissionAccess;
        }

        return permissionAccess || checkFacilitatorAccess(facilitatorProfiles);
    };

    let hasPermission = checkVisibility(permissionsRequired, permissionsAnyOf, facilitatorAccess);
    let childPermissionCount = 0;
    let hasChildPermission = !((dropdownItems?.length ?? 0) > 0);
    let subChildPermissionCount = 0;
    let hasSubChildPermission = true;

    // Check if has at least one permission from children
    if (!hasChildPermission && dropdownItems && hasPermission) {
        hasSubChildPermission = false;

        dropdownItems.forEach((item: DropdownMenuItemType) => {
            if (checkVisibility(item.permissions_required, item.permissions_any_of, item.facilitator_access)) {
                hasChildPermission = true;
            }

            childPermissionCount += (item.permissions_required?.length ?? 0) + (item.permissions_any_of?.length ?? 0);

            // Ahora verificamos los permisos en los subMenuItems
            if (item.subMenuItems) {
                item.subMenuItems.forEach((subItem) => {
                    if (checkVisibility(subItem.permissions_required, subItem.permissions_any_of, subItem.facilitator_access)) {
                        hasSubChildPermission = true;
                    }

                    subChildPermissionCount += (subItem.permissions_required?.length ?? 0) + (subItem.permissions_any_of?.length ?? 0);
                });
            }
        });

        if (subChildPermissionCount === 0) hasSubChildPermission = true;
        if (childPermissionCount === 0) hasChildPermission = true;

    }



    return hasPermission && hasChildPermission && hasSubChildPermission;
}
